import type { Country, Shipment } from "../types";
import type { OrderCargoItem, OrderFormValues } from "../types/cmrpreview";
import type {
  ContainerLineOrderRef,
  ContainerLineStop,
  ContainerOrder,
  ContainerOrderDirection,
  ContainerOrderLine,
  ContainerStopRole,
} from "../types/containerOrders";
import { activeStopsOf } from "./containerLineSteps";
import { createCmrPdf } from "./pdfGenerator";

/**
 * A container, expressed as a CMR.
 *
 * The consignment note is the shipment module's document, drawn by
 * `CmrPreview` and `createCmrPdf` from a shipment-shaped object. Rather than
 * fork either — the note's design is fixed by the convention it prints, and two
 * copies of it would drift — a container is translated into that shape here and
 * the existing pair render it unchanged.
 *
 * Everything is optional. A shipment gates its CMR behind a Yup schema because
 * the note is the shipment's deliverable and half of one is worse than none; a
 * container's note is a convenience printed off a record that is filled in over
 * three stops, so a missing field prints as the empty box it is on the paper
 * form. That is why every field below resolves to `""` rather than to
 * `undefined` — the PDF generator dereferences `delivery.address.street`
 * without guarding it, and an absent branch would throw where a blank string
 * simply draws nothing.
 */

/** A populated country ref arrives as an object; both renderers want the id. */
const countryId = (country: unknown): string => {
  if (!country) return "";
  if (typeof country === "string") return country;
  return (country as { _id?: string })._id ?? "";
};

/** One stop, as a CMR party: name, address, and the moment it is due. */
const partyFromStop = (stop?: ContainerLineStop) => ({
  companyName: stop?.companyName ?? "",
  address: {
    street: stop?.address?.street ?? "",
    city: stop?.address?.city ?? "",
    state: stop?.address?.state ?? "",
    postalCode: stop?.address?.postalCode ?? "",
    country: countryId(stop?.address?.country),
  },
  date: stop?.date ?? "",
  time: stop?.time ?? "",
});

/**
 * Which two of the container's stops the goods actually travel between.
 *
 * A container run is three stops or more, a consignment note is two — and which
 * two depends on the direction, because only one leg of the run carries goods:
 *
 *   · Outbound — the box is collected empty, loaded at the client's site, and
 *     returned full. The carriage is loading site → drop-off terminal.
 *   · Inbound — the box is collected full, unloaded at the client's site, and
 *     returned empty. The carriage is pickup terminal → loading site.
 *
 * With no direction to go on (the order was not passed) the run is read
 * end-to-end, first stop → last stop. That is the container's itinerary rather
 * than its consignment, but it is the one reading that is never wrong about
 * where the truck went.
 *
 * Each end falls back along the route when the terminal it names is not being
 * used. A container that is not returning to a terminal still carries goods
 * somewhere, and the note has to say where: the outbound leg then ends at
 * whatever the container does last, and the inbound leg starts wherever it
 * actually starts. Printing an empty box instead would leave the driver holding
 * a consignment note with no consignee — which is the one field on it that is
 * never optional in practice, whatever the form allows.
 *
 * Added stops are not ends of the leg unless they turn out to be the last thing
 * the container does. A weighbridge between the client and the terminal is on
 * the way, not a destination, and a note that named it as one would misdescribe
 * the carriage.
 */
const legOf = (
  line: ContainerOrderLine,
  direction?: ContainerOrderDirection,
): { from?: ContainerLineStop; to?: ContainerLineStop } => {
  const stops = activeStopsOf(line);
  const roleOf = (role: ContainerStopRole) =>
    stops.find((stop) => stop.role === role);

  const first = stops[0];
  const last = stops[stops.length - 1];
  const loadingSite = roleOf("loadingSite") ?? line.loadingSite;

  if (direction === "Outbound") {
    return { from: loadingSite, to: roleOf("terminalDropoff") ?? last };
  }
  if (direction === "Inbound") {
    return { from: roleOf("terminalPickup") ?? first, to: loadingSite };
  }
  return { from: first, to: last };
};

const cargoFromLine = (line: ContainerOrderLine): OrderCargoItem[] =>
  (line.cargoItems ?? []).map((item) => ({
    marks: item.marks ?? "",
    quantity: item.quantity ?? undefined,
    type: item.type || "",
    description: item.description ?? "",
    weightKg: item.weightKg ?? undefined,
    volumeM3: item.volumeM3 ?? undefined,
  }));

/** The truck's plate, whether the reference came back populated or as an id. */
const plateOf = (line: ContainerOrderLine): string =>
  line.vehicleId && typeof line.vehicleId === "object"
    ? (line.vehicleId.registrationNumber ?? "")
    : "";

/**
 * The order fields the note draws from, as either shape the app holds them in.
 *
 * The container's own page is given the trimmed `ContainerLineOrderRef` the API
 * returns beside the container; the batch tables have the whole order. Both
 * carry what is wanted here, so the parameter takes either.
 */
export type CmrOrderContext = Partial<ContainerOrder> &
  Partial<ContainerLineOrderRef>;

/**
 * One container as the CMR renderers want it.
 *
 * The identity is the container's line number in both boxes that state one —
 * the preview prints `shipmentNumber` and the PDF prints `referenceNumber`, and
 * they must not disagree about which container this is.
 */
export function buildContainerCmrOrder(
  line: ContainerOrderLine,
  order?: CmrOrderContext,
): OrderFormValues {
  const { from, to } = legOf(line, order?.direction);

  return {
    shipmentNumber: line.lineNumber || "",
    referenceNumber: line.lineNumber || "",
    type: "International",
    pickup: partyFromStop(from),
    delivery: partyFromStop(to),
    carrier: {
      // No carrier party exists on a container — the run is dispatched to a
      // driver and a truck, not contracted out to a haulier — so boxes 16 and
      // 17 print empty, as they do on a blank form.
      companyName: "",
      address: { street: "", city: "", state: "", postalCode: "", country: "" },
      truckLicensePlate: plateOf(line),
      trailerLicensePlate: "",
      containerNumber: line.containerNumber ?? "",
    },
    cargoItems: cargoFromLine(line),
    additionalNotes: line.notes ?? "",
    pricing: { currency: "EUR" },
  };
}

/** `CMR_<container>_<yyyy-mm-dd>.pdf`, or the order number if unnumbered. */
export const containerCmrFileName = (line: ContainerOrderLine): string => {
  const id = line.containerNumber || line.lineNumber || line._id;
  return `CMR_${id}_${new Date().toISOString().split("T")[0]}.pdf`;
};

/** The note's bytes, for a preview iframe or a download. */
export const createContainerCmrPdf = (
  line: ContainerOrderLine,
  order: CmrOrderContext | undefined,
  countries: Country[],
) =>
  createCmrPdf(
    buildContainerCmrOrder(line, order) as unknown as Shipment,
    countries,
  );

/**
 * Draw the note and hand it to the browser.
 *
 * Same mechanism the shipment table's download uses, and the object URL is
 * revoked straight after the click — a batch of thirty containers downloaded
 * one after another would otherwise hold thirty PDFs in memory for the life of
 * the page.
 */
export async function downloadContainerCmrPdf(
  line: ContainerOrderLine,
  order: CmrOrderContext | undefined,
  countries: Country[],
): Promise<void> {
  const pdfBytes = await createContainerCmrPdf(line, order, countries);
  const blob = new Blob([new Uint8Array(pdfBytes)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = containerCmrFileName(line);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
